import React from 'react';
import { Typography, Box, Grid } from '@mui/material';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { DashboardWidget } from './components/DashboardWidget';
import { StatsCard } from '../../shared/components/ui/StatsCard';
import { dashboardSections } from '../config/dashboard-widgets.config';

/**
 * DashboardPage component
 * Main dashboard view for authenticated administrators
 * Design aligned with Stitch: Nexus Admin design system
 */
export const DashboardPage: React.FC = () => {
  return (
    <Box sx={{ p: 4, maxWidth: 1440 }}>
      {/* Management Section */}
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontSize: '32px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: '#0b1c30',
            lineHeight: '40px',
            letterSpacing: '-0.02em',
            mb: 4,
          }}
        >
          Management
        </Typography>

        <Grid container spacing={3}>
          {/* Featured Account Card */}
          <Grid item xs={12} md={6} lg={4}>
            <DashboardWidget
              widget={{
                id: 'accounts',
                icon: CorporateFareIcon,
                title: 'Accounts',
                description:
                  'Manage account organizations and their configurations. View usage metrics, billing details, and enterprise structure.',
                actionLabel: 'View Accounts',
                actionPath: '/accounts',
              }}
            />
          </Grid>

          {/* Hero Card - Enterprise Visibility */}
          <Grid item xs={12} md={6} lg={8}>
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0052ff 0%, #003ec7 100%)',
                minHeight: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                p: 4,
              }}
            >
              {/* Background Pattern */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.1,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'right', maxWidth: 400 }}>
                <Typography
                  sx={{
                    fontSize: '24px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    color: '#ffffff',
                    lineHeight: '32px',
                    letterSpacing: '-0.01em',
                    mb: 2,
                  }}
                >
                  Enterprise Visibility
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    color: '#dde1ff',
                    lineHeight: '24px',
                    mb: 3,
                  }}
                >
                  Gain full operational transparency across your entire global organization with
                  Style Connect&apos;s advanced monitoring tools.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(8px)',
                      borderRadius: '9999px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      fontSize: '12px',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      color: '#ffffff',
                    }}
                  >
                    99.9% Uptime
                  </Box>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(8px)',
                      borderRadius: '9999px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      fontSize: '12px',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      color: '#ffffff',
                    }}
                  >
                    Global Ready
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* System Section */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: '32px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              color: '#0b1c30',
              lineHeight: '40px',
              letterSpacing: '-0.02em',
            }}
          >
            System
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              color: '#003ec7',
              '&:hover': { textDecoration: 'underline', cursor: 'pointer' },
            }}
          >
            System Status: Healthy
          </Typography>
        </Box>

        {/* Widgets Grid */}
        <Grid container spacing={3}>
          {dashboardSections
            .find((s) => s.id === 'system')
            ?.widgets.map((widget) => (
              <Grid item xs={12} md={4} key={widget.id}>
                <DashboardWidget widget={widget} />
              </Grid>
            ))}
        </Grid>
      </Box>

      {/* Bottom Stats */}
      <Box sx={{ mt: 6 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <StatsCard label="Total Organizations" value="1,422" change={4} />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatsCard label="Active API Tokens" value="8,940" change={12} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #c3c5d9',
                borderRadius: '12px',
                p: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    color: '#737688',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5,
                  }}
                >
                  Last System Backup
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    color: '#0b1c30',
                  }}
                >
                  Today, 04:00 AM (UTC)
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  border: '1px solid #003ec7',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  color: '#003ec7',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 62, 199, 0.05)',
                  },
                }}
              >
                Run Manual Backup
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
